import $ from 'jquery';
import {inflateRaw} from "pako";

/**
 * Copyright (c) 2006-2020, JGraph Ltd
 * Copyright (c) 2006-2020, Gaudenz Alder
 *
 * Usage: DiagramEditor.editElement(elt) where elt is an img or object with
 * a data URI src or data attribute or an svg node with a content attribute.
 *
 * See https://jgraph.github.io/drawio-integration/javascript.html
 */
export function DiagramEditor(elt, config, ui, formatVerifier, done, initialized, exported, urlParams) {
    this.elt = elt;
    this.config = (config != null) ? config : this.config;
    this.ui = (ui != null) ? ui : this.ui;
    this.formatVerifier = (formatVerifier != null) ? formatVerifier : this.formatVerifier;
    this.done = (done != null) ? done : this.done;
    this.initialized = (initialized != null) ? initialized : this.initialized;
    this.exported = (exported != null) ? exported : this.exported;
    this.urlParams = urlParams;

    let self = this;

    this.handleMessageEvent = function (evt) {
        if (self.frame != null && evt.source == self.frame.contentWindow &&
            evt.data.length > 0) {
            try {
                var msg = JSON.parse(evt.data);

                if (msg != null) {
                    self.handleMessage(msg);
                }
            } catch (e) {
                console.error(e);
            }
        }
    };
    this.handleUnload = function (evt) {
        evt.preventDefault();
        evt.returnValue = '';
    }
};

/**
 * Static method to edit the diagram in the given img or object.
 */
DiagramEditor.editElement = function (elt, config, ui, formatVerifier, done, initialized, exported, urlParams) {
    if (!elt.diagramEditorStarting) {
        elt.diagramEditorStarting = true;

        return new DiagramEditor(elt, config, ui, formatVerifier, done, initialized, exported, urlParams).editElement(elt);
    }
};

DiagramEditor.prototype.diagramBounds = {};
/**
 * Global configuration.
 */
DiagramEditor.prototype.config = null;

/**
 * Protocol and domain to use.
 */
DiagramEditor.prototype.drawDomain = 'https://embed.diagrams.net/';

/**
 * UI theme to be use.
 */
DiagramEditor.prototype.ui = 'min';

/**
 * Contains XML for pending image export.
 */
DiagramEditor.prototype.xml = null;

/**
 * Format to use.
 */
DiagramEditor.prototype.format = 'xml';

/**
 * Specifies if libraries should be enabled.
 */
DiagramEditor.prototype.libraries = true;

DiagramEditor.prototype.returnbounds = true;

/**
 * CSS style for the iframe.
 */
DiagramEditor.prototype.frameStyle = 'position:absolute;border:0;width:100%;height:100%;';

/**
 * Adds the iframe and starts editing.
 */
DiagramEditor.prototype.editElement = function (elem) {
    let src = this.getElementData(elem);
    this.startElement = elem;
    let fmt = this.format;

    if (src.substring(0, 15) === 'data:image/png;') {
        fmt = 'xmlpng';
    } else if (src.substring(0, 19) === 'data:image/svg+xml;' ||
        elem.nodeName.toLowerCase() == 'svg') {
        fmt = 'xmlsvg';
    }
    this.startEditing(elem, src, fmt);

    return this;
};

/**
 * Adds the iframe and starts editing.
 */
DiagramEditor.prototype.getElementData = function (elem) {
    let name = elem.nodeName.toLowerCase();

    return elem.getAttribute((name == 'svg') ? 'content' :
        ((name == 'img') ? 'src' : 'data'));
};

/**
 * Adds the iframe and starts editing.
 */
DiagramEditor.prototype.setElementData = function (elem, data) {
    let name = elem.nodeName.toLowerCase();

    if (name == 'svg') {
        elem.outerHTML = atob(data.substring(data.indexOf(',') + 1));
    } else {
        elem.setAttribute((name == 'img') ? 'src' : 'data', data);
    }

    return elem;
};

/**
 * Starts the editor for the given data.
 */
DiagramEditor.prototype.startEditing = function (elem, data, format, title) {
    if (this.frame == null) {
        window.addEventListener('message', this.handleMessageEvent);
        window.addEventListener('beforeunload', this.handleUnload);
        this.format = (format != null) ? format : this.format;
        this.title = (title != null) ? title : this.title;
        this.data = data;

        this.frame = this.createFrame(
            this.getFrameUrl(),
            this.getFrameStyle());
        this.elt.appendChild(this.frame);
        this.setWaiting(true);
    }
};

/**
 * Updates the waiting cursor.
 */
DiagramEditor.prototype.setWaiting = function (waiting) {
    if (this.startElement != null) {
        // Redirect cursor to parent for SVG and object
        let elt = this.startElement;
        let name = elt.nodeName.toLowerCase();

        if (name == 'svg' || name == 'object') {
            elt = elt.parentNode;
        }

        if (elt != null) {
            if (waiting) {
                this.frame.style.pointerEvents = 'none';
                this.previousCursor = elt.style.cursor;
                elt.style.cursor = 'wait';
            } else {
                elt.style.cursor = this.previousCursor;
                this.frame.style.pointerEvents = '';
            }
        }
    }
};

/**
 * Updates the waiting cursor.
 */
DiagramEditor.prototype.setActive = function (active) {
    if (active) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = this.previousOverflow;
    }
};

/**
 * Removes the iframe.
 */
DiagramEditor.prototype.stopEditing = function () {
    if (this.frame != null) {
        window.removeEventListener('message', this.handleMessageEvent);
        this.frame.parentNode.removeChild(this.frame);
        this.setActive(false);
        this.frame = null;
    }
};


/**
 * Send the given message to the iframe.
 */

DiagramEditor.prototype.postMessage = function (msg) {
    if (this.frame != null) {
        this.frame.contentWindow.postMessage(JSON.stringify(msg), '*');
    }
};


DiagramEditor.prototype.getBounds = function () {
    return this.diagramBounds;
};

/**
 * Returns the diagram data.
 */
DiagramEditor.prototype.getData = function () {
    return this.data;
};

/**
 * Returns the title for the editor.
 */
DiagramEditor.prototype.getTitle = function () {
    return this.title;
};

/**
 * Returns the CSS style for the iframe.
 */
DiagramEditor.prototype.getFrameStyle = function () {
    return this.frameStyle + ';left:' +
        document.body.scrollLeft + 'px;top:' +
        document.body.scrollTop + 'px;';
};

/**
 * Returns the URL for the iframe.
 */
DiagramEditor.prototype.getFrameUrl = function () {
    let url = this.drawDomain + '?proto=json&spin=1';

    if (this.ui != null) {
        url += '&ui=' + this.ui;
    }

    if (this.libraries != null) {
        url += '&libraries=1';
    }

    if (this.returnbounds != null) {
        url += '&returnbounds=1';
    }

    if (this.config != null) {
        url += '&configure=1';
    }

    if (this.urlParams != null) {
        url += '&' + this.urlParams.join('&');
    }

    return url;
};

/**
 * Creates the iframe.
 */
DiagramEditor.prototype.createFrame = function (url, style) {
    let frame = document.createElement('iframe');
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('style', style);
    frame.setAttribute('src', url);
    frame.setAttribute('sandbox', "allow-same-origin allow-scripts")

    return frame;
};

/**
 * Sets the status of the editor.
 */
DiagramEditor.prototype.setStatus = function (messageKey, modified) {
    this.postMessage({action: 'status', messageKey: messageKey, modified: modified});
};

/**
 * Handles the given message.
 */
DiagramEditor.prototype.handleMessage = function (msg) {
    if (msg.event == 'configure') {
        this.configureEditor();
    } else if (msg.event == 'init') {
        this.initializeEditor();
    } else if (msg.event == 'load') {
        this.diagramBounds = {
            x: msg.bounds.x,
            y: msg.bounds.y,
            width: msg.bounds.width,
            height: msg.bounds.height,
            translateX: msg.translate.x,
            translateY: msg.translate.y,
        }
        this.initialized();
    } else if (msg.event == 'autosave') {
        this.save(msg.xml, true, this.startElement);
    } else if (msg.event == 'export') {
        this.exported(msg.data);
        /*this.setElementData(this.startElement, msg.data);
        this.stopEditing();
        this.xml = null;*/
    } else if (msg.event == 'save') {
        this.save(msg.xml, false, this.startElement);
        this.xml = msg.xml;
        /*if (msg.exit) {
            msg.event = 'exit';
        } else {
            //this.setStatus('allChangesSaved', false);
        }*/
    } else if (msg.event == 'merge' && msg.error) {
        console.log(msg.error)
    }

    if (msg.event == 'exit') {
        if (this.format != 'xml') {
            if (this.xml != null) {
                this.postMessage({
                    action: 'export', format: this.format,
                    xml: this.xml, spinKey: 'export'
                });
            } else {
                this.stopEditing(msg);
            }
        } else {
            if (msg.modified == null || msg.modified) {
                this.save(msg.xml, false, this.startElement);
            }

            this.stopEditing(msg);
        }
    }
};

/**
 * Posts configure message to editor.
 */
DiagramEditor.prototype.configureEditor = function () {
    this.postMessage({action: 'configure', config: this.config});
};

/**
 * Posts load message to editor.
 */
DiagramEditor.prototype.initializeEditor = function () {
    this.postMessage({
        action: 'load', autosave: 0, saveAndExit: '1',
        modified: 'unsavedChanges', xml: this.getData(),
        title: this.getTitle()
    });
    this.setWaiting(false);
    this.setActive(true);
    delete this.elt.diagramEditorStarting;
};

/**
 * Saves the given data.
 */
DiagramEditor.prototype.save = function (data, draft, elt) {
    let dataObject = {data: data};
    let isDataValid = this.formatVerifier(dataObject);
    this.postMessage({action: 'merge', xml: data});
    this.postMessage({action: 'merge', xml: dataObject.data});
    if (isDataValid) {
        this.setElementData(elt, data);
        this.done(data, draft, elt);
        this.setStatus('allChangesSaved', false);
    } else {

        this.setStatus('unsavedChanges', true);
    }
};


/**
 * Invoked before save to check data formatting.
 */
DiagramEditor.prototype.formatVerifier = function (data, draft, elt) {
    return true;
};

/**
 * Invoked after save.
 */
DiagramEditor.prototype.done = function (data, draft, elt) {
    // hook for subclassers
};

/**
 * Invoked after the editor has sent the init message.
 */
DiagramEditor.prototype.initialized = function () {
    // hook for subclassers
};

/**
 * Invoked after the editor has sent the export message.
 */
DiagramEditor.prototype.exported = function (data) {
    // hook for subclassers
};

export function decompressXML(dataString) {
    let parser = new DOMParser();
    let xmlDoc = parser.parseFromString(dataString, "text/xml");
    let encodedDataTag = $(xmlDoc).find("diagram");

    // Get the base64 diagram content
    let b64Data = encodedDataTag[0].innerHTML;
    // Decode base64 (convert ascii to binary)
    let strData;
    try {
        let asciiData = atob(b64Data);
        // Convert binary string to character-number array
        let charData = asciiData.split('').map(function (x) {
            return x.charCodeAt(0);
        });
        // Turn number array into byte-array
        let binData = new Uint8Array(charData);
        // Pako magic
        let data = inflateRaw(binData);
        // Convert gunzipped byteArray back to uri-formatted string
        let uriData = new TextDecoder().decode(data);
        // Convert uri-formatted string to plain text
        strData = decodeURIComponent(uriData);
    } catch (e) {
        strData = b64Data;
    }

    let parsed = parser.parseFromString(strData, "text/xml")
    encodedDataTag.empty().append(parsed.firstChild);
    return xmlDoc;
}
